/* eslint-disable max-classes-per-file */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, OperatorFunction } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ConfigurationFacade } from '../facades/configuration.facade';
import { TranslatableService } from '../i18n/TranslatableService';
import { TranslatableStringType } from '../i18n/types';
import { catchErrorAndShowMessage } from '../misc/errorHandlers';
import { HttpAuthHeader } from '../misc/httpauthorizationheader';
import { AppState } from '../state/appState';

type HttpHeaderOption = { headers: HttpHeaders };

export class Cache {
  cacheTime = 30 * 60 * 1000; // 30 min cache

  constructor(
    private data: Array<any> = null,
    private timestamp: number | null = null,
  ) {
    if (timestamp) {
      this.timestamp = this.timestamp + this.cacheTime;
    }
  }

  public getValue(): Array<any> {
    return this.data;
  }

  public hasCacheExpired(): boolean {
    return this.timestamp === null || this.timestamp < Date.now();
  }
}

export class CacheContainer {
  container = new Map<string, BehaviorSubject<Cache>>();

  constructor(keys: string[]) {
    keys.forEach((key) => this.container.set(key, new BehaviorSubject<Cache>(new Cache())));
  }

  getSubject(key: string): BehaviorSubject<Cache> {
    return this.container.get(key);
  }

  getCacheObject(key: string): Cache {
    return this.getSubject(key).getValue();
  }

  update(key: string, cacheObject: Cache) {
    this.getSubject(key).next(cacheObject);
  }

  clear(key: string) {
    this.update(key, new Cache());
  }

  clearAll() {
    this.container.forEach((_, key) => this.update(key, new Cache()));
  }
}

@Injectable()
export class BLService {
  private cacheContainer: CacheContainer;
  private DEFAULT_CACHE_KEY = 'default';
  protected store: Store<AppState>;
  protected http: HttpClient;
  protected translationService: TranslatableService;
  protected configurationFacade: ConfigurationFacade;
  protected httpAuthHeader: HttpAuthHeader;

  constructor() {
    this.store = inject(Store);
    this.http = inject(HttpClient);
    this.configurationFacade = inject(ConfigurationFacade);
    this.httpAuthHeader = inject(HttpAuthHeader);
  }

  public useCache(keys: string[] = [this.DEFAULT_CACHE_KEY]): void {
    this.cacheContainer = new CacheContainer(keys);
  }

  public catchErrorAndShowMessage(): OperatorFunction<any, any> {
    return catchErrorAndShowMessage({ store: this.store });
  }

  protected hasCacheExpired(key: string = this.DEFAULT_CACHE_KEY): boolean {
    return this.cacheContainer.getCacheObject(key).hasCacheExpired();
  }

  protected updateCache(result: any[], key: string = this.DEFAULT_CACHE_KEY) {
    const hasResultValues = Array.isArray(result) && result.length > 0;

    if (!hasResultValues) {
      this.clearCache(key);
      return;
    }

    this.cacheContainer.update(key, new Cache(result, Date.now()));
  }

  protected clearCache(key: string = this.DEFAULT_CACHE_KEY) {
    this.cacheContainer.clear(key);
  }

  protected getCacheObservable<T>(key: string = this.DEFAULT_CACHE_KEY): Observable<T> {
    return this.cacheContainer
      .getSubject(key)
      .asObservable()
      .pipe(
        take(1),
        map((cacheObject: Cache) => <T>(<unknown>cacheObject.getValue())),
      );
  }

  protected getCacheValue<T>(key: string = this.DEFAULT_CACHE_KEY): T {
    return <T>(<unknown>this.cacheContainer.getCacheObject(key).getValue());
  }

  protected useTranslation(translationScope: string): void {
    this.translationService = new TranslatableService(translationScope);
  }

  protected translate(
    key: string,
    options: { params?: any; useComponentScope?: boolean } = {},
  ): TranslatableStringType {
    if (!this.translationService) {
      throw new Error('Translation service is not initialized. Please provide a translation scope.');
    }
    return this.translationService.translate(key, options);
  }

  protected httpGet<T, O extends HttpHeaderOption = HttpHeaderOption>(url: string, options?: O): Observable<T> {
    const serverUrl = this.configurationFacade.getServerUrl(url);
    return this.http
      .get<T>(serverUrl, options ?? this.httpAuthHeader.getHeader())
      .pipe(this.catchErrorAndShowMessage());
  }

  protected httpPost<T, O extends HttpHeaderOption = HttpHeaderOption>(
    url: string,
    body: any,
    options?: O,
    errorHandler: OperatorFunction<T, T> = this.catchErrorAndShowMessage(),
  ): Observable<T> {
    const serverUrl = this.configurationFacade.getServerUrl(url);
    return this.http.post<T>(serverUrl, body, options ?? this.httpAuthHeader.getHeader()).pipe(errorHandler);
  }

  protected httpPut<T, O extends HttpHeaderOption = HttpHeaderOption>(
    url: string,
    body: any,
    options?: O,
    errorHandler: OperatorFunction<T, T> = this.catchErrorAndShowMessage(),
  ): Observable<T> {
    const serverUrl = this.configurationFacade.getServerUrl(url);
    return this.http.put<T>(serverUrl, body, options ?? this.httpAuthHeader.getHeader()).pipe(errorHandler);
  }

  protected httpPatch<T, O extends HttpHeaderOption = HttpHeaderOption>(
    url: string,
    body: any,
    options?: O,
    errorHandler: OperatorFunction<T, T> = this.catchErrorAndShowMessage(),
  ): Observable<T> {
    const serverUrl = this.configurationFacade.getServerUrl(url);
    return this.http.patch<T>(serverUrl, body, options ?? this.httpAuthHeader.getHeader()).pipe(errorHandler);
  }

  protected httpDelete<T, O extends HttpHeaderOption = HttpHeaderOption>(
    url: string,
    options?: O,
    errorHandler: OperatorFunction<T, T> = this.catchErrorAndShowMessage(),
  ): Observable<T> {
    const serverUrl = this.configurationFacade.getServerUrl(url);
    return this.http.delete<T>(serverUrl, options ?? this.httpAuthHeader.getHeader()).pipe(errorHandler);
  }
}
